/**
 * 音源ファイルの置き場所・鳴らし方・クレジット（全県共通）。
 *
 * - ファイルは public/assets/audio/ に置く。**置いたものから順に**合成音と入れ替わる
 *   （1つだけ置いても動く。無いものは今までどおり合成音）
 * - 県ごとに変えたいときだけ、ステージ定義の `audio` で上書きする（例：県ごとのBGM）
 * - 置く前に、素材サイトの規約と、下の AUDIO_CREDITS を必ず確認する
 *   （詳しくは public/assets/audio/README.md）
 */

import type { StageAudioPaths } from './types';
import { scaleSemitone, YO_SCALE_SEMITONES } from '../audio/scale';

const DIR = '/assets/audio/';

/** 全県共通のファイル名。 */
export const DEFAULT_AUDIO_PATHS: Readonly<Required<StageAudioPaths>> = {
  bgm: `${DIR}bgm_main.mp3`,
  drop: `${DIR}se_drop.mp3`,
  merge: `${DIR}se_merge.mp3`,
  combo: `${DIR}se_combo.mp3`,
  top: `${DIR}se_top.mp3`,
  gameOver: `${DIR}se_gameover.mp3`,
  bonus: `${DIR}se_bonus.mp3`,
  unlock: `${DIR}se_unlock.mp3`,
};

/** 共通のファイル名に、ステージ側の上書きを重ねる（置いてあるかどうかは見ない）。 */
export function audioPathsFor(stage?: StageAudioPaths): StageAudioPaths {
  return { ...DEFAULT_AUDIO_PATHS, ...(stage ?? {}) };
}

/**
 * 実際に読みに行くパス。**AUDIO_FILE_SOURCES に書いたファイルだけ**を返す。
 *
 * 置いていないファイルを毎回取りに行くと、47県ぶんの起動で無駄なリクエストが7〜8本ずつ出る
 * （Cloudflare Pages は無いファイルにトップページの HTML を返すので、その分の通信も乗る）。
 * 出どころの表は「置いたファイルの一覧」を兼ねる（置いたのに書き忘れたらテストが落ちる）。
 */
export function resolveAudioPaths(
  stage?: StageAudioPaths,
  sources: Readonly<Record<string, unknown>> = AUDIO_FILE_SOURCES,
): StageAudioPaths {
  const out: StageAudioPaths = {};
  for (const [key, path] of Object.entries(audioPathsFor(stage)) as [
    keyof StageAudioPaths,
    string,
  ][]) {
    const file = path.split('/').pop() ?? '';
    if (Object.prototype.hasOwnProperty.call(sources, file)) out[key] = path;
  }
  return out;
}

/** 効果音ファイルの鳴らし方。 */
export interface SampleTuning {
  /** 音量の補正（素材ごとの大きさの差をここでそろえる）。 */
  gain: number;
  /**
   * 料理のレベルで音程を変えるか。
   * 合成音と同じく、陽音階で1段ずつ上げる。連鎖すると旋律に聞こえる仕組みを、ファイルでも残す。
   */
  pitchByLevel?: boolean;
  /** 同時に鳴らす数の上限（連鎖で音が団子にならないように）。 */
  maxVoices: number;
}

/**
 * 音程の基準になるレベル。このレベルで素材そのままの高さ（再生速度1.0）。
 * Lv2〜Lv12 は、ここから上下に振れる。
 */
export const PITCH_BASE_LEVEL = 7;

/**
 * 音程の振れ幅（1.0 = 合成音と同じ。Lv2→Lv12で2オクターブ）。
 * 素材は再生速度で音程を変えるので、振りすぎると低い側が間延びし、高い側が細くなる。
 * 0.5 で Lv2 が半オクターブ下（速度0.71）、Lv12 が半オクターブ上（速度1.41）。
 */
export const PITCH_DEPTH = 0.5;

export const SAMPLE_TUNING: Readonly<Record<keyof Omit<StageAudioPaths, 'bgm'>, SampleTuning>> = {
  drop: { gain: 0.8, maxVoices: 3 },
  merge: { gain: 1, pitchByLevel: true, maxVoices: 5 },
  combo: { gain: 1, pitchByLevel: true, maxVoices: 5 },
  top: { gain: 1, maxVoices: 1 },
  gameOver: { gain: 1, maxVoices: 1 },
  bonus: { gain: 1, maxVoices: 2 },
  unlock: { gain: 0.8, maxVoices: 2 },
};

/** 料理のレベルから、素材の再生速度を求める（純粋な関数）。 */
export function playbackRateForLevel(level: number, depth: number = PITCH_DEPTH): number {
  const step = (lv: number) => Math.max(0, Math.round(lv) - 2);
  const semis =
    scaleSemitone(YO_SCALE_SEMITONES, step(level)) -
    scaleSemitone(YO_SCALE_SEMITONES, step(PITCH_BASE_LEVEL));
  return Math.pow(2, (semis * depth) / 12);
}

/** 音源のクレジット。CC BY の素材を使ったら、必ずここに書く（県えらびの画面の下に出る）。 */
export interface AudioCredit {
  /** 表示名（例：「OtoLogic」）。 */
  name: string;
  url: string;
  /** 何に使ったか（例：「効果音」「BGM」）。 */
  role: string;
}

export const AUDIO_CREDITS: readonly AudioCredit[] = [
  // 例: { name: 'OtoLogic', url: 'https://otologic.jp', role: '効果音' },
];

/**
 * 置いた音源ファイルの出どころ（ファイル名 → 素材の情報）。
 *
 * public/assets/audio/ にファイルを置いたら、**必ずここに1行書く。** 書き忘れはテストで落ちる
 * （tests/audioFiles.test.ts）。料理や景色の出典と同じで、「どこの素材か」を聞かれたら出せるようにしておく。
 * `creditRequired: true` の素材は、AUDIO_CREDITS に同じ name の行が無いとテストで落ちる。
 */
export interface AudioFileSource {
  /** 素材サイト・作者（AUDIO_CREDITS の name と合わせる）。 */
  name: string;
  /** 素材そのもののページ。 */
  url: string;
  /** ライセンス（例：「効果音ラボ利用規約」「CC BY 4.0」）。 */
  license: string;
  /** クレジット表示が利用条件か。 */
  creditRequired: boolean;
  /** 加工したこと（切り出し・音量・フェードなど）。 */
  edits?: string;
  /** 規約を確認した日（YYYY-MM-DD）。 */
  checkedAt: string;
}

export const AUDIO_FILE_SOURCES: Readonly<Record<string, AudioFileSource>> = {
  // 例:
  // 'se_merge.mp3': {
  //   name: 'OtoLogic', url: 'https://otologic.jp/free/se/....html', license: 'CC BY 4.0',
  //   creditRequired: true, edits: '先頭の無音を切った・-3dB', checkedAt: '2026-10-01',
  // },
};
